import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Storage as StorageIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as TestIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface Connection {
  id: string;
  name: string;
  type: 'd1' | 'mysql';
  status: 'connected' | 'disconnected' | 'active'; // 支持active状态
  host?: string;
  port?: number;
  database: string;
  createdAt: string;
  lastConnected: string;
  isWorkersBound?: boolean; // 是否为Workers绑定的D1数据库
  binding?: string; // Workers绑定名称
}

interface CreateConnectionData {
  name: string;
  type: 'd1' | 'mysql';
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
}

export const Connections: React.FC = () => {
  const { token, user } = useAuth();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const [formData, setFormData] = useState<CreateConnectionData>({
    name: '',
    type: 'mysql',
    host: '',
    port: 3306,
    database: '',
    username: '',
    password: ''
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  
  // 通知状态
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // 移动到顶部import

  // 检查管理员权限
  const isAdmin = user?.role.name === 'Administrator';

  // API调用辅助函数
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: '请求失败' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // 加载连接列表
  const loadConnections = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/connections');
      console.log('连接API响应:', response); // 调试日志
      
      // API返回格式是 { data: { connections: [...] } }
      const rawConnections = response.data?.connections || response.connections || [];
      
      // 数据格式转换：后端 -> 前端
      const mappedConnections = rawConnections.map((conn: any) => ({
        id: conn.id,
        name: conn.name,
        type: conn.type,
        status: (conn.status === 'active' || conn.status === 'connected') ? 'connected' : 'disconnected', // 🔧 修复：支持connected和active状态
        host: conn.host,
        port: conn.port,
        database: conn.database || conn.database_name, // 兼容两种字段名
        createdAt: typeof conn.createdAt === 'number' ? 
          new Date(conn.createdAt * 1000).toISOString() : 
          conn.createdAt || new Date().toISOString(),
        lastConnected: conn.lastConnected || conn.last_connected || new Date().toISOString()
      }));
      
      console.log('映射后的连接:', mappedConnections); // 调试日志
      setConnections(mappedConnections);
    } catch (error) {
      console.error('加载连接失败:', error);
      showSnackbar('加载连接列表失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 创建连接
  const createConnection = async () => {
    try {
      const data = await apiCall('/api/connections', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      
      showSnackbar('连接创建成功', 'success');
      setOpenDialog(false);
      resetForm();
      loadConnections();
    } catch (error) {
      console.error('创建连接失败:', error);
      showSnackbar((error as Error).message || '创建连接失败', 'error');
    }
  };

  // 更新连接
  const updateConnection = async () => {
    if (!editingConnection) return;

    try {
      await apiCall(`/api/connections/${editingConnection.id}`, {
        method: 'PUT',
        body: JSON.stringify(formData),
      });

      showSnackbar('连接更新成功', 'success');
      setOpenDialog(false);
      setEditingConnection(null);
      resetForm();
      loadConnections();
    } catch (error) {
      console.error('更新连接失败:', error);
      showSnackbar((error as Error).message || '更新连接失败', 'error');
    }
  };

  // 删除连接
  const deleteConnection = async (connectionId: string) => {
    if (!window.confirm('确定要删除这个连接吗？此操作不可撤销。')) {
      return;
    }

    try {
      await apiCall(`/api/connections/${connectionId}`, {
        method: 'DELETE',
      });

      showSnackbar('连接删除成功', 'success');
      loadConnections();
    } catch (error) {
      console.error('删除连接失败:', error);
      showSnackbar((error as Error).message || '删除连接失败', 'error');
    }
  };

  // 测试连接
  const testConnection = async (connectionId: string) => {
    try {
      setTestingConnection(connectionId);
      const data = await apiCall(`/api/connections/${connectionId}/test`, {
        method: 'POST',
      });
      
      if (data.success) {
        showSnackbar(`${data.message} (${data.responseTime}ms)`, 'success');
        // 重新加载连接列表以更新状态
        loadConnections();
      } else {
        showSnackbar(`连接测试失败: ${data.message}`, 'error');
      }
    } catch (error) {
      console.error('测试连接失败:', error);
      showSnackbar((error as Error).message || '测试连接失败', 'error');
    } finally {
      setTestingConnection(null);
    }
  };

  // 显示通知
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // 重置表单
  const resetForm = () => {
    setFormData({
      name: '',
      type: 'mysql',
      host: '',
      port: 3306,
      database: '',
      username: '',
      password: ''
    });
  };

  // 打开创建对话框
  const handleCreate = () => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以管理连接', 'error');
      return;
    }
    setEditingConnection(null);
    resetForm();
    setOpenDialog(true);
  };

  // 打开编辑对话框
  const handleEdit = (connection: Connection) => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以管理连接', 'error');
      return;
    }
    
    setEditingConnection(connection);
    setFormData({
      name: connection.name,
      type: connection.type,
      host: connection.host || '',
      port: connection.port || 3306,
      database: connection.database,
      username: '', // 安全考虑，不预填用户名
      password: ''  // 安全考虑，不预填密码
    });
    setOpenDialog(true);
    setAnchorEl(null);
  };

  // 处理删除
  const handleDelete = (connection: Connection) => {
    if (!isAdmin) {
      showSnackbar('权限不足：只有管理员可以管理连接', 'error');
      return;
    }
    
    deleteConnection(connection.id);
    setAnchorEl(null);
  };

  // 处理表单提交
  const handleSubmit = () => {
    if (!formData.name || !formData.database) {
      showSnackbar('请填写连接名称和数据库名', 'error');
      return;
    }

    if (formData.type === 'mysql' && (!formData.host || !formData.username)) {
      showSnackbar('MySQL连接需要填写主机和用户名', 'error');
      return;
    }

    if (editingConnection) {
      updateConnection();
    } else {
      createConnection();
    }
  };

  // 菜单操作
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, connection: Connection) => {
    setAnchorEl(event.currentTarget);
    setSelectedConnection(connection);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConnection(null);
  };

  // 获取连接类型颜色
  const getConnectionTypeColor = (type: string) => {
    return type === 'd1' ? 'primary' : 'secondary';
  };

  // 获取状态颜色
  const getStatusColor = (status: string) => {
    return (status === 'connected' || status === 'active') ? 'success' : 'error';
  };
  
  // 获取状态显示文本
  const getStatusText = (status: string) => {
    return (status === 'connected' || status === 'active') ? '已连接' : '已断开';
  };

  // 格式化日期
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  useEffect(() => {
    loadConnections();
  }, []);

  if (loading) {
    return <LoadingSpinner message="加载连接数据..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          连接管理
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={loadConnections}
            sx={{ mr: 1 }}
          >
            刷新
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disabled={!isAdmin}
          >
            添加连接
          </Button>
        </Box>
      </Box>

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          您当前的权限只能查看连接信息。如需管理连接，请联系管理员。
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 3 }}>
        {connections.map((connection) => (
          <Card key={connection.id}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StorageIcon sx={{ mr: 1, color: `${getConnectionTypeColor(connection.type)}.main` }} />
                  <Typography variant="h6" component="div">
                    {connection.name}
                  </Typography>
                </Box>
                {isAdmin && !connection.isWorkersBound && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, connection)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={connection.type.toUpperCase()}
                  color={getConnectionTypeColor(connection.type)}
                  size="small"
                />
                <Chip
                  label={getStatusText(connection.status)}
                  color={getStatusColor(connection.status)}
                  size="small"
                  icon={(connection.status === 'connected' || connection.status === 'active') ? <CheckCircleIcon /> : <ErrorIcon />}
                />
                {connection.isWorkersBound && (
                  <Chip
                    label="Workers绑定"
                    color="secondary"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>数据库:</strong> {connection.database}
              </Typography>
              
              {connection.isWorkersBound && connection.binding && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>绑定名称:</strong> {connection.binding}
                </Typography>
              )}
              
              {connection.host && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>主机:</strong> {connection.host}{connection.port ? `:${connection.port}` : ''}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>创建时间:</strong> {formatDate(connection.createdAt)}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                <strong>最后连接:</strong> {formatDate(connection.lastConnected)}
              </Typography>
            </CardContent>
            
            <CardActions>
              <Button
                size="small"
                startIcon={testingConnection === connection.id ? 
                  <CircularProgress size={16} /> : <TestIcon />
                }
                onClick={() => testConnection(connection.id)}
                disabled={testingConnection === connection.id}
              >
                {testingConnection === connection.id ? '测试中...' : '测试连接'}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* 操作菜单 */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEdit(selectedConnection!)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>编辑</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedConnection!)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>删除</ListItemText>
        </MenuItem>
      </Menu>

      {/* 创建/编辑对话框 */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingConnection ? '编辑连接' : '创建连接'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="连接名称"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <FormControl fullWidth required>
              <InputLabel>连接类型</InputLabel>
              <Select
                value={formData.type}
                label="连接类型"
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'd1' | 'mysql' })}
              >
                <MenuItem value="d1">Cloudflare D1</MenuItem>
                <MenuItem value="mysql">MySQL</MenuItem>
              </Select>
            </FormControl>

            {formData.type === 'mysql' && (
              <>
                <TextField
                  label="主机地址"
                  value={formData.host}
                  onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                  fullWidth
                  required
                />

                <TextField
                  label="端口"
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 3306 })}
                  fullWidth
                />

                <TextField
                  label="用户名"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  fullWidth
                  required
                />

                <TextField
                  label="密码"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  fullWidth
                />
              </>
            )}

            <TextField
              label="数据库名"
              value={formData.database}
              onChange={(e) => setFormData({ ...formData, database: e.target.value })}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingConnection ? '更新' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 